import { ExchangeRates, convertToTargetCurrency } from './exchangeRates';
import { COUNTRY_DEFAULT_CURRENCY } from './countries';

export { COUNTRY_DEFAULT_CURRENCY };

/**
 * Builds personalized Actual Budget category groups from onboarding answers.
 * Category notes use Actual's goal-template syntax (e.g. `#template 500`).
 *
 * Questionnaire answers are saved in the browser (localStorage) so users can
 * continue later. They are never sent to Nimah servers — only the generated
 * category and account names are sent when the user explicitly creates them.
 */

export type BillFrequency = 'monthly' | 'quarterly' | 'yearly';

export type HousingType = 'rent' | 'mortgage' | 'own';

export interface RecurringAmount {
  amount: number | null;
  /** Day of month (1–28) for monthly bills */
  dueDay: number | null;
  /** Month (1–12) for yearly bills */
  dueMonth: number | null;
  frequency: BillFrequency;
  /** Currency this amount is paid in (may differ from budget currency) */
  currency?: string;
}

export interface RentalUnitAnswer {
  id: string;
  /** Country code from locales/countries.json (e.g. EG, SA) */
  country: string;
  label: string;
  housingType: HousingType;
  payment: RecurringAmount;
}

export interface UtilityAnswer {
  enabled: boolean;
  bill: RecurringAmount;
}

export interface LivingLocationAnswer {
  id: string;
  country: string;
  label: string;
  electricity: UtilityAnswer;
  water: UtilityAnswer;
  internet: UtilityAnswer;
  phone: UtilityAnswer;
  /** Typical monthly grocery spend (optional template) */
  groceries: RecurringAmount;
  transportation: RecurringAmount;
}

export interface CarAnswer {
  id: string;
  name: string;
  country: string;
  fuel: RecurringAmount;
  insurance: RecurringAmount;
  maintenance: RecurringAmount;
  loan: RecurringAmount;
}

export interface PropertyAnswer {
  id: string;
  name: string;
  country: string;
  hasInstallments: boolean;
  installments: RecurringAmount;
  hasMaintenance: boolean;
  maintenance: RecurringAmount;
  hasPropertyTax: boolean;
  propertyTax: RecurringAmount;
  hasInsurance: boolean;
  insurance: RecurringAmount;
  rentsOut: boolean;
  rent: RecurringAmount;
  rentalExpenses: RecurringAmount;
}

export interface ChildEducationAnswer {
  id: string;
  name: string;
  yearsOfSchoolLeft: number | null;
  avgSchoolYearly: number | null;
  avgSchoolCurrency?: string;
  schoolEndMonth: number | null;
  yearsOfUniversityLeft: number | null;
  avgUniversityYearly: number | null;
  avgUniversityCurrency?: string;
  universityEndMonth: number | null;
  supplies: RecurringAmount;
  activities: RecurringAmount;
}

export interface IncomeSourceAnswer {
  id: string;
  type: 'salary' | 'family' | 'business' | 'dividends' | 'rental' | 'other';
  label: string;
  /** Optional expected monthly amount (for notes / planning) */
  expectedMonthly: number | null;
  /** Currency for expectedMonthly */
  expectedCurrency?: string;
}

export type BankAccountType =
  | 'checking'
  | 'savings'
  | 'creditCard'
  | 'investment'
  | 'loan'
  | 'cash'
  | 'other';

export interface BankAccountAnswer {
  id: string;
  name: string;
  country: string;
  bankId?: string;
  bankName?: string;
  accountType: BankAccountType;
  /** Create this account in Actual Budget during setup */
  createInActual: boolean;
  /** Current balance as of today (for budget reconciliation) */
  currentBalance: number | null;
  balanceCurrency?: string;
  /** Name of uploaded statement file (metadata only — file is not persisted) */
  statementFileName?: string;
  statementSkipped?: boolean;
}

export type DebtSource = 'creditCard' | 'overdraft' | 'other';

export interface DebtAnswer {
  id: string;
  name: string;
  source: DebtSource;
  /** When auto-detected from a bank account balance */
  linkedAccountId?: string;
  /** Total amount still owed */
  totalOwed: number | null;
  totalOwedCurrency?: string;
  /** Regular payment toward this debt */
  payment: RecurringAmount;
}

export interface GoalAnswer {
  id: string;
  name: string;
  /** Built-in savings goal seeded on the goals step */
  isDefault?: boolean;
  /** Optional savings target (e.g. car down payment) */
  targetAmount: number | null;
  targetCurrency?: string;
  /** How much the user plans to set aside toward this goal */
  monthlyContribution: RecurringAmount;
}

export interface OnboardingAnswers {
  /** Country where the user mainly lives */
  primaryCountry: string;
  /** Currency they mainly spend in — preselected on amount fields */
  primaryCurrency: string;
  rentalUnits: RentalUnitAnswer[];
  livingLocations: LivingLocationAnswer[];
  cars: CarAnswer[];
  properties: PropertyAnswer[];
  children: ChildEducationAnswer[];
  incomeSources: IncomeSourceAnswer[];
  bankAccounts: BankAccountAnswer[];
  debts: DebtAnswer[];
  goals: GoalAnswer[];
  includeShopping: boolean;
  includeHealth: boolean;
  includeGifts: boolean;
  includeTravel: boolean;
  includeSubscriptions: boolean;
  subscriptionBill: RecurringAmount;
  includeCharity: boolean;
  includeAssets: boolean;
  includeOther: boolean;
}

export interface CategoryDraft {
  name: string;
  note?: string;
}

export interface CategoryGroupDraft {
  id: string;
  name: string;
  categories: CategoryDraft[];
  enabled: boolean;
  isCustom: boolean;
  /** Actual income category group */
  isIncome?: boolean;
}

export interface AccountDraft {
  name: string;
  country: string;
  accountType: BankAccountType;
}

export type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

export type CountryLabelFn = (countryCode: string) => string;

export function emptyRecurring(
  frequency: BillFrequency = 'monthly',
  currency?: string
): RecurringAmount {
  return { amount: null, dueDay: null, dueMonth: null, frequency, currency };
}

export function createDefaultAnswers(): OnboardingAnswers {
  return {
    primaryCountry: '',
    primaryCurrency: 'USD',
    rentalUnits: [],
    livingLocations: [],
    cars: [],
    properties: [],
    children: [],
    incomeSources: [createEmptyIncomeSource(0, 'USD')],
    bankAccounts: [],
    debts: [],
    goals: [],
    includeShopping: true,
    includeHealth: true,
    includeGifts: true,
    includeTravel: true,
    includeSubscriptions: true,
    subscriptionBill: emptyRecurring('monthly', 'USD'),
    includeCharity: true,
    includeAssets: true,
    includeOther: true
  };
}

/**
 * When the main spending currency changes, update amounts that still use the
 * previous default (or have no currency set). Explicit per-field overrides are kept.
 */
export function applyPrimaryCurrency(
  answers: OnboardingAnswers,
  nextCurrency: string,
  previousCurrency: string
): OnboardingAnswers {
  const mapBill = (bill: RecurringAmount): RecurringAmount => {
    if (!bill.currency || bill.currency === previousCurrency) {
      return { ...bill, currency: nextCurrency };
    }
    return bill;
  };
  const mapUtility = (utility: UtilityAnswer): UtilityAnswer => ({
    ...utility,
    bill: mapBill(utility.bill)
  });

  return {
    ...answers,
    primaryCurrency: nextCurrency,
    subscriptionBill: mapBill(answers.subscriptionBill),
    rentalUnits: answers.rentalUnits.map(unit => ({
      ...unit,
      payment: mapBill(unit.payment)
    })),
    livingLocations: answers.livingLocations.map(location => ({
      ...location,
      electricity: mapUtility(location.electricity),
      water: mapUtility(location.water),
      internet: mapUtility(location.internet),
      phone: mapUtility(location.phone),
      groceries: mapBill(location.groceries),
      transportation: mapBill(location.transportation)
    })),
    cars: answers.cars.map(car => ({
      ...car,
      fuel: mapBill(car.fuel),
      insurance: mapBill(car.insurance),
      maintenance: mapBill(car.maintenance),
      loan: mapBill(car.loan)
    })),
    properties: answers.properties.map(property => ({
      ...property,
      installments: mapBill(property.installments),
      maintenance: mapBill(property.maintenance),
      propertyTax: mapBill(property.propertyTax),
      insurance: mapBill(property.insurance),
      rent: mapBill(property.rent),
      rentalExpenses: mapBill(property.rentalExpenses)
    })),
    children: answers.children.map(child => ({
      ...child,
      avgSchoolCurrency:
        !child.avgSchoolCurrency || child.avgSchoolCurrency === previousCurrency
          ? nextCurrency
          : child.avgSchoolCurrency,
      avgUniversityCurrency:
        !child.avgUniversityCurrency || child.avgUniversityCurrency === previousCurrency
          ? nextCurrency
          : child.avgUniversityCurrency,
      supplies: mapBill(child.supplies),
      activities: mapBill(child.activities)
    })),
    incomeSources: answers.incomeSources.map(source => ({
      ...source,
      expectedCurrency:
        !source.expectedCurrency || source.expectedCurrency === previousCurrency
          ? nextCurrency
          : source.expectedCurrency
    }))
  };
}

export function createEmptyRentalUnit(index: number, currency = 'USD'): RentalUnitAnswer {
  return {
    id: `rental-${Date.now()}-${index}`,
    country: '',
    label: '',
    housingType: 'rent',
    payment: emptyRecurring('monthly', currency)
  };
}

export function createEmptyLivingLocation(index: number, currency = 'USD'): LivingLocationAnswer {
  return {
    id: `location-${Date.now()}-${index}`,
    country: '',
    label: '',
    electricity: { enabled: true, bill: emptyRecurring('monthly', currency) },
    water: { enabled: true, bill: emptyRecurring('monthly', currency) },
    internet: { enabled: true, bill: emptyRecurring('monthly', currency) },
    phone: { enabled: true, bill: emptyRecurring('monthly', currency) },
    groceries: emptyRecurring('monthly', currency),
    transportation: emptyRecurring('monthly', currency)
  };
}

export function createEmptyCar(index: number, currency = 'USD'): CarAnswer {
  return {
    id: `car-${Date.now()}-${index}`,
    name: '',
    country: '',
    fuel: emptyRecurring('monthly', currency),
    insurance: emptyRecurring('yearly', currency),
    maintenance: emptyRecurring('yearly', currency),
    loan: emptyRecurring('monthly', currency)
  };
}

export function createEmptyProperty(index: number, currency = 'USD'): PropertyAnswer {
  return {
    id: `property-${Date.now()}-${index}`,
    name: '',
    country: '',
    hasInstallments: true,
    installments: emptyRecurring('monthly', currency),
    hasMaintenance: true,
    maintenance: emptyRecurring('yearly', currency),
    hasPropertyTax: false,
    propertyTax: emptyRecurring('yearly', currency),
    hasInsurance: false,
    insurance: emptyRecurring('yearly', currency),
    rentsOut: false,
    rent: emptyRecurring('monthly', currency),
    rentalExpenses: emptyRecurring('monthly', currency)
  };
}

export function createEmptyChild(index: number, currency = 'USD'): ChildEducationAnswer {
  return {
    id: `child-${Date.now()}-${index}`,
    name: '',
    yearsOfSchoolLeft: null,
    avgSchoolYearly: null,
    avgSchoolCurrency: currency,
    schoolEndMonth: 6,
    yearsOfUniversityLeft: null,
    avgUniversityYearly: null,
    avgUniversityCurrency: currency,
    universityEndMonth: 6,
    supplies: emptyRecurring('yearly', currency),
    activities: emptyRecurring('yearly', currency)
  };
}

export function createEmptyIncomeSource(index: number, currency = 'USD'): IncomeSourceAnswer {
  return {
    id: `income-${Date.now()}-${index}`,
    type: 'salary',
    label: '',
    expectedMonthly: null,
    expectedCurrency: currency
  };
}

export function createEmptyBankAccount(
  index: number,
  defaultCountry = '',
  defaultCurrency = 'USD'
): BankAccountAnswer {
  return {
    id: `account-${Date.now()}-${index}`,
    name: '',
    country: defaultCountry,
    bankId: '',
    bankName: '',
    accountType: 'checking',
    createInActual: true,
    currentBalance: null,
    balanceCurrency: defaultCurrency
  };
}

export function createEmptyDebt(index: number, defaultCurrency = 'USD'): DebtAnswer {
  return {
    id: `debt-${Date.now()}-${index}`,
    name: '',
    source: 'other',
    totalOwed: null,
    totalOwedCurrency: defaultCurrency,
    payment: emptyRecurring('monthly', defaultCurrency)
  };
}

export function createDefaultSavingsGoal(
  defaultCurrency = 'USD',
  name = ''
): GoalAnswer {
  return {
    id: 'goal-default-savings',
    name,
    isDefault: true,
    targetAmount: null,
    targetCurrency: defaultCurrency,
    monthlyContribution: emptyRecurring('monthly', defaultCurrency)
  };
}

export function createEmptyGoal(index: number, defaultCurrency = 'USD'): GoalAnswer {
  return {
    id: `goal-${Date.now()}-${index}`,
    name: '',
    targetAmount: null,
    targetCurrency: defaultCurrency,
    monthlyContribution: emptyRecurring('monthly', defaultCurrency)
  };
}

/** Ensure the goals step always starts with a default savings goal. */
export function ensureDefaultGoals(
  goals: GoalAnswer[] | undefined,
  defaultCurrency = 'USD',
  defaultSavingsName = ''
): GoalAnswer[] {
  const list = goals ?? [];
  if (list.some(goal => goal.isDefault)) return list;
  return [createDefaultSavingsGoal(defaultCurrency, defaultSavingsName), ...list];
}

/** Detect credit-card balances and checking overdrafts as debts. */
export function deriveDebtsFromAccounts(
  accounts: BankAccountAnswer[],
  defaultCurrency = 'USD'
): DebtAnswer[] {
  const debts: DebtAnswer[] = [];

  accounts.forEach(account => {
    if (!account.createInActual || !account.name.trim() || account.currentBalance == null) {
      return;
    }

    const currency = account.balanceCurrency || defaultCurrency;
    const balance = account.currentBalance;

    if (account.accountType === 'creditCard' && balance > 0) {
      debts.push({
        id: `debt-account-${account.id}`,
        name: account.name.trim(),
        source: 'creditCard',
        linkedAccountId: account.id,
        totalOwed: balance,
        totalOwedCurrency: currency,
        payment: emptyRecurring('monthly', currency)
      });
      return;
    }

    if (account.accountType === 'checking' && balance < 0) {
      debts.push({
        id: `debt-account-${account.id}`,
        name: account.name.trim(),
        source: 'overdraft',
        linkedAccountId: account.id,
        totalOwed: Math.abs(balance),
        totalOwedCurrency: currency,
        payment: emptyRecurring('monthly', currency)
      });
    }
  });

  return debts;
}

/** Merge account-derived debts with manually added debts. */
export function mergeDebtsWithDerived(
  existing: DebtAnswer[],
  accounts: BankAccountAnswer[],
  defaultCurrency = 'USD'
): DebtAnswer[] {
  const derived = deriveDebtsFromAccounts(accounts, defaultCurrency);
  const derivedByAccount = new Map(
    derived.filter(debt => debt.linkedAccountId).map(debt => [debt.linkedAccountId!, debt])
  );
  const manual = existing.filter(debt => !debt.linkedAccountId);
  const mergedDerived = derived.map(derivedDebt => {
    const prior = existing.find(debt => debt.linkedAccountId === derivedDebt.linkedAccountId);
    if (!prior) return derivedDebt;
    return {
      ...derivedDebt,
      payment: prior.payment,
      name: prior.name.trim() ? prior.name : derivedDebt.name
    };
  });

  return [...mergedDerived, ...manual];
}

/** Whether an account type should be tracked off-budget in Actual Budget */
export function isOffBudgetAccountType(type: BankAccountType): boolean {
  return type === 'investment' || type === 'loan';
}

/** True when every on-budget account has a balance entered (minimum for onboarding). */
export function hasMinimumAccountBalances(accounts: BankAccountAnswer[]): boolean {
  const active = accounts.filter(account => account.createInActual && account.name.trim());
  if (active.length === 0) return true;
  return active.every(account => account.currentBalance != null);
}

/** Normalize a numeric amount for Actual templates (no thousands separators). */
export function formatTemplateAmount(amount: number): string {
  if (!Number.isFinite(amount) || amount <= 0) return '';
  const rounded = Math.round(amount * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
}

/** Convert an amount into budget currency for template notes. */
export function amountInBudgetCurrency(
  amount: number,
  amountCurrency: string | undefined,
  budgetCurrency: string | undefined,
  exchangeRates?: ExchangeRates | null
): number | null {
  if (!Number.isFinite(amount) || amount <= 0) return null;
  const from = amountCurrency || budgetCurrency;
  if (!from || !budgetCurrency) return amount;
  if (from === budgetCurrency) return amount;
  if (!exchangeRates) return null;
  return convertToTargetCurrency(amount, from, budgetCurrency, exchangeRates);
}

/**
 * Build an Actual `#template` note from a recurring amount.
 * Monthly → `#template 500`
 * Yearly → `#template 1200 by YYYY-MM repeat every year`
 *
 * Amounts in other currencies are converted to the budget currency when
 * exchange rates are provided.
 */
export function buildTemplateNote(
  bill: RecurringAmount,
  now = new Date(),
  budgetCurrency?: string,
  exchangeRates?: ExchangeRates | null
): string | undefined {
  if (bill.amount == null || bill.amount <= 0) return undefined;

  const converted = amountInBudgetCurrency(
    bill.amount,
    bill.currency || budgetCurrency,
    budgetCurrency,
    exchangeRates
  );
  if (converted == null) return undefined;
  const amount = formatTemplateAmount(converted);
  if (!amount) return undefined;

  if (bill.frequency === 'monthly') {
    return `#template ${amount}`;
  }

  if (bill.frequency === 'quarterly') {
    const monthlyEquivalent = formatTemplateAmount(converted / 3);
    if (!monthlyEquivalent) return undefined;
    return `#template ${monthlyEquivalent}`;
  }

  const month = bill.dueMonth && bill.dueMonth >= 1 && bill.dueMonth <= 12
    ? bill.dueMonth
    : now.getMonth() + 1;
  const year = now.getFullYear();
  const targetYear = month < now.getMonth() + 1 ? year + 1 : year;
  const mm = String(month).padStart(2, '0');
  return `#template ${amount} by ${targetYear}-${mm} repeat every year`;
}

/** Build a one-time yearly savings target for a specific calendar year. */
export function buildYearlyTargetNote(
  amount: number,
  targetYear: number,
  endMonth = 6,
  options?: {
    amountCurrency?: string;
    budgetCurrency?: string;
    exchangeRates?: ExchangeRates | null;
  }
): string | undefined {
  const converted = amountInBudgetCurrency(
    amount,
    options?.amountCurrency || options?.budgetCurrency,
    options?.budgetCurrency,
    options?.exchangeRates
  );
  const formatted = converted != null ? formatTemplateAmount(converted) : '';
  if (!formatted) return undefined;
  const mm = String(Math.min(12, Math.max(1, endMonth))).padStart(2, '0');
  return `#template ${formatted} by ${targetYear}-${mm}`;
}

function cat(
  name: string,
  bill?: RecurringAmount,
  now?: Date,
  budgetCurrency?: string,
  exchangeRates?: ExchangeRates | null
): CategoryDraft;
function cat(name: string, note?: string): CategoryDraft;
function cat(
  name: string,
  billOrNote?: RecurringAmount | string,
  now = new Date(),
  budgetCurrency?: string,
  exchangeRates?: ExchangeRates | null
): CategoryDraft {
  if (typeof billOrNote === 'string') {
    return billOrNote ? { name, note: billOrNote } : { name };
  }
  const note = billOrNote
    ? buildTemplateNote(billOrNote, now, budgetCurrency, exchangeRates)
    : undefined;
  return note ? { name, note } : { name };
}

function locationDisplayName(
  location: LivingLocationAnswer,
  countryLabel: CountryLabelFn,
  t: TranslateFn,
  index: number
): string {
  if (location.label.trim()) return location.label.trim();
  if (location.country) return countryLabel(location.country);
  return t('builder.defaults.locationName', { ns: 'onboarding', number: index + 1 });
}

function rentalsForCountry(rentals: RentalUnitAnswer[], country: string): RentalUnitAnswer[] {
  return rentals.filter(unit => unit.country === country);
}

function buildSchoolYearCategories(
  childName: string,
  yearsLeft: number,
  avgYearly: number | null,
  endMonth: number | null,
  labelKey: string,
  t: TranslateFn,
  now: Date,
  amountCurrency?: string,
  budgetCurrency?: string,
  exchangeRates?: ExchangeRates | null
): CategoryDraft[] {
  if (!yearsLeft || yearsLeft <= 0) return [];
  const amount = avgYearly ?? 0;
  const month = endMonth && endMonth >= 1 && endMonth <= 12 ? endMonth : 6;
  const startYear = now.getFullYear() + 1;

  return Array.from({ length: yearsLeft }, (_, index) => {
    const targetYear = startYear + index;
    const name = t(labelKey, { ns: 'onboarding', name: childName, year: targetYear });
    const note =
      amount > 0
        ? buildYearlyTargetNote(amount, targetYear, month, {
            amountCurrency,
            budgetCurrency,
            exchangeRates
          })
        : undefined;
    return note ? { name, note } : { name };
  });
}

/**
 * Map questionnaire answers into editable category group drafts.
 */
export function buildCategoryGroupsFromAnswers(
  answers: OnboardingAnswers,
  t: TranslateFn,
  countryLabel: CountryLabelFn,
  now = new Date(),
  budgetCurrency = answers.primaryCurrency || 'USD',
  exchangeRates?: ExchangeRates | null
): CategoryGroupDraft[] {
  const groups: CategoryGroupDraft[] = [];
  const locationCountries = new Set(answers.livingLocations.map(loc => loc.country).filter(Boolean));
  const billCat = (name: string, bill?: RecurringAmount) =>
    cat(name, bill, now, budgetCurrency, exchangeRates);

  // Ensure every rental country gets a basics group (even without a living location)
  answers.rentalUnits.forEach(unit => {
    if (unit.country) locationCountries.add(unit.country);
  });

  const processedCountries = new Set<string>();

  answers.livingLocations.forEach((location, index) => {
    if (!location.country) return;
    processedCountries.add(location.country);
    const display = locationDisplayName(location, countryLabel, t, index);
    const categories: CategoryDraft[] = [];

    rentalsForCountry(answers.rentalUnits, location.country).forEach(unit => {
      const housingName = unit.housingType === 'mortgage'
        ? t('builder.items.mortgage', { ns: 'onboarding' })
        : t('builder.items.rent', { ns: 'onboarding' });
      const rentLabel = unit.label.trim()
        ? `${housingName} — ${unit.label.trim()}`
        : housingName;
      categories.push(billCat(rentLabel, unit.payment));
    });

    categories.push(billCat(t('builder.items.groceries', { ns: 'onboarding' }), location.groceries));
    categories.push(billCat(t('builder.items.transportation', { ns: 'onboarding' }), location.transportation));
    categories.push(cat(t('builder.items.eatingOut', { ns: 'onboarding' })));

    if (location.electricity.enabled) {
      categories.push(billCat(t('builder.items.electricity', { ns: 'onboarding' }), location.electricity.bill));
    }
    if (location.water.enabled) {
      categories.push(billCat(t('builder.items.water', { ns: 'onboarding' }), location.water.bill));
    }
    if (location.internet.enabled) {
      categories.push(billCat(t('builder.items.internet', { ns: 'onboarding' }), location.internet.bill));
    }
    if (location.phone.enabled) {
      categories.push(billCat(t('builder.items.phone', { ns: 'onboarding' }), location.phone.bill));
    }

    groups.push({
      id: `basics-${location.id}`,
      name: t('builder.groups.regularBasics', { ns: 'onboarding', location: display }),
      categories,
      enabled: true,
      isCustom: false
    });
  });

  // Rental-only countries without a living location
  locationCountries.forEach(country => {
    if (processedCountries.has(country)) return;
    const rentals = rentalsForCountry(answers.rentalUnits, country);
    if (rentals.length === 0) return;

    const categories = rentals.map(unit => {
      const housingName = unit.housingType === 'mortgage'
        ? t('builder.items.mortgage', { ns: 'onboarding' })
        : t('builder.items.rent', { ns: 'onboarding' });
      const rentLabel = unit.label.trim()
        ? `${housingName} — ${unit.label.trim()}`
        : housingName;
      return billCat(rentLabel, unit.payment);
    });

    groups.push({
      id: `basics-rental-${country}`,
      name: t('builder.groups.regularBasics', { ns: 'onboarding', location: countryLabel(country) }),
      categories,
      enabled: true,
      isCustom: false
    });
  });

  answers.cars.forEach((car, index) => {
    const carName = car.name.trim()
      || t('builder.defaults.carName', { ns: 'onboarding', number: index + 1 });
    const countrySuffix = car.country ? ` (${countryLabel(car.country)})` : '';
    groups.push({
      id: `car-${car.id}`,
      name: `${carName}${countrySuffix}`,
      categories: [
        billCat(t('builder.items.fuel', { ns: 'onboarding' }), car.fuel),
        billCat(t('builder.items.maintenance', { ns: 'onboarding' }), car.maintenance),
        cat(t('builder.items.tires', { ns: 'onboarding' })),
        cat(t('builder.items.replacement', { ns: 'onboarding' })),
        billCat(t('builder.items.insurance', { ns: 'onboarding' }), car.insurance),
        billCat(t('builder.items.carLoan', { ns: 'onboarding' }), car.loan),
        cat(t('builder.items.parkingFees', { ns: 'onboarding' }))
      ],
      enabled: true,
      isCustom: false
    });
  });

  answers.properties.forEach((property, index) => {
    const propertyName = property.name.trim()
      || t('builder.defaults.propertyName', { ns: 'onboarding', number: index + 1 });
    const countrySuffix = property.country ? ` (${countryLabel(property.country)})` : '';
    const categories: CategoryDraft[] = [];

    if (property.hasInstallments) {
      categories.push(billCat(t('builder.items.installments', { ns: 'onboarding' }), property.installments));
    }
    if (property.hasMaintenance) {
      categories.push(billCat(t('builder.items.propertyMaintenance', { ns: 'onboarding' }), property.maintenance));
    }
    if (property.hasPropertyTax) {
      categories.push(billCat(t('builder.items.propertyTax', { ns: 'onboarding' }), property.propertyTax));
    }
    if (property.hasInsurance) {
      categories.push(billCat(t('builder.items.propertyInsurance', { ns: 'onboarding' }), property.insurance));
    }
    if (property.rentsOut) {
      categories.push(billCat(t('builder.items.rentCollected', { ns: 'onboarding' }), property.rent));
      categories.push(billCat(t('builder.items.rentalExpenses', { ns: 'onboarding' }), property.rentalExpenses));
    }
    if (categories.length === 0) {
      categories.push(cat(t('builder.items.propertyMaintenance', { ns: 'onboarding' })));
    }

    groups.push({
      id: `property-${property.id}`,
      name: `${propertyName}${countrySuffix}`,
      categories,
      enabled: true,
      isCustom: false
    });
  });

  answers.children.forEach((child, index) => {
    const childName = child.name.trim()
      || t('builder.defaults.childName', { ns: 'onboarding', number: index + 1 });
    const categories: CategoryDraft[] = [
      ...buildSchoolYearCategories(
        childName,
        child.yearsOfSchoolLeft ?? 0,
        child.avgSchoolYearly,
        child.schoolEndMonth,
        'builder.items.schoolYear',
        t,
        now,
        child.avgSchoolCurrency || answers.primaryCurrency,
        budgetCurrency,
        exchangeRates
      ),
      ...buildSchoolYearCategories(
        childName,
        child.yearsOfUniversityLeft ?? 0,
        child.avgUniversityYearly,
        child.universityEndMonth,
        'builder.items.universityYear',
        t,
        now,
        child.avgUniversityCurrency || answers.primaryCurrency,
        budgetCurrency,
        exchangeRates
      ),
      billCat(t('builder.items.schoolSupplies', { ns: 'onboarding' }), child.supplies),
      billCat(t('builder.items.schoolActivities', { ns: 'onboarding' }), child.activities)
    ];

    if (categories.length === 0) return;

    groups.push({
      id: `child-${child.id}`,
      name: t('builder.groups.schooling', { ns: 'onboarding', name: childName }),
      categories,
      enabled: true,
      isCustom: false
    });
  });

  if (answers.incomeSources.length > 0) {
    groups.push({
      id: 'income',
      name: t('builder.groups.income', { ns: 'onboarding' }),
      categories: answers.incomeSources.map(source => {
        const typeLabel = t(`builder.incomeTypes.${source.type}`, { ns: 'onboarding' });
        const name = source.label.trim() || typeLabel;
        const amountCurrency = source.expectedCurrency || answers.primaryCurrency;
        const converted =
          source.expectedMonthly != null && source.expectedMonthly > 0
            ? amountInBudgetCurrency(
                source.expectedMonthly,
                amountCurrency,
                budgetCurrency,
                exchangeRates
              )
            : null;
        const note =
          converted != null ? `#template ${formatTemplateAmount(converted)}` : undefined;
        return note ? { name, note } : { name };
      }),
      enabled: true,
      isCustom: false,
      isIncome: true
    });
  }

  if (answers.includeShopping) {
    groups.push({
      id: 'shopping',
      name: t('builder.groups.shopping', { ns: 'onboarding' }),
      categories: [
        cat(t('builder.items.clothing', { ns: 'onboarding' })),
        cat(t('builder.items.household', { ns: 'onboarding' })),
        cat(t('builder.items.electronics', { ns: 'onboarding' }))
      ],
      enabled: true,
      isCustom: false
    });
  }

  if (answers.includeHealth) {
    groups.push({
      id: 'health',
      name: t('builder.groups.health', { ns: 'onboarding' }),
      categories: [
        cat(t('builder.items.medical', { ns: 'onboarding' })),
        cat(t('builder.items.pharmacy', { ns: 'onboarding' })),
        cat(t('builder.items.fitness', { ns: 'onboarding' })),
        cat(t('builder.items.personalCare', { ns: 'onboarding' }))
      ],
      enabled: true,
      isCustom: false
    });
  }

  if (answers.includeGifts) {
    groups.push({
      id: 'gifts',
      name: t('builder.groups.gifts', { ns: 'onboarding' }),
      categories: [
        cat(t('builder.items.gifts', { ns: 'onboarding' })),
        cat(t('builder.items.donationsOccasional', { ns: 'onboarding' }))
      ],
      enabled: true,
      isCustom: false
    });
  }

  if (answers.includeTravel) {
    groups.push({
      id: 'travel',
      name: t('builder.groups.travel', { ns: 'onboarding' }),
      categories: [
        cat(t('builder.items.flights', { ns: 'onboarding' })),
        cat(t('builder.items.hotels', { ns: 'onboarding' })),
        cat(t('builder.items.travelSpending', { ns: 'onboarding' }))
      ],
      enabled: true,
      isCustom: false
    });
  }

  if (answers.includeSubscriptions) {
    groups.push({
      id: 'subscriptions',
      name: t('builder.groups.subscriptions', { ns: 'onboarding' }),
      categories: [
        billCat(t('builder.items.streaming', { ns: 'onboarding' }), answers.subscriptionBill),
        cat(t('builder.items.software', { ns: 'onboarding' })),
        cat(t('builder.items.memberships', { ns: 'onboarding' }))
      ],
      enabled: true,
      isCustom: false
    });
  }

  if (answers.includeAssets) {
    groups.push({
      id: 'assets',
      name: t('builder.groups.assets', { ns: 'onboarding' }),
      categories: [
        cat(t('builder.items.emergencyFund', { ns: 'onboarding' })),
        cat(t('builder.items.investments', { ns: 'onboarding' })),
        cat(t('builder.items.savingsGoals', { ns: 'onboarding' }))
      ],
      enabled: true,
      isCustom: false
    });
  }

  if (answers.includeCharity) {
    groups.push({
      id: 'charity',
      name: t('builder.groups.charity', { ns: 'onboarding' }),
      categories: [
        cat(t('builder.items.charity', { ns: 'onboarding' })),
        cat(t('builder.items.zakat', { ns: 'onboarding' }))
      ],
      enabled: true,
      isCustom: false
    });
  }

  if (answers.includeOther) {
    groups.push({
      id: 'other',
      name: t('builder.groups.other', { ns: 'onboarding' }),
      categories: [
        cat(t('builder.items.misc', { ns: 'onboarding' })),
        cat(t('builder.items.fees', { ns: 'onboarding' }))
      ],
      enabled: true,
      isCustom: false
    });
  }

  return groups;
}

export function buildAccountsFromAnswers(
  answers: OnboardingAnswers,
  countryLabel: CountryLabelFn
): AccountDraft[] {
  return answers.bankAccounts
    .filter(account => account.createInActual && account.name.trim())
    .map(account => ({
      name: account.name.trim(),
      country: account.country ? countryLabel(account.country) : '',
      accountType: account.accountType || 'checking'
    }));
}

export function buildDebtCategoryGroup(
  debts: DebtAnswer[],
  t: TranslateFn,
  budgetCurrency = 'USD',
  exchangeRates?: ExchangeRates | null,
  now = new Date()
): CategoryGroupDraft | null {
  const billCat = (name: string, bill?: RecurringAmount) =>
    cat(name, bill, now, budgetCurrency, exchangeRates);

  const categories = debts
    .filter(debt => debt.name.trim())
    .map(debt => billCat(debt.name.trim(), debt.payment))
    .filter(category => category.name);

  if (categories.length === 0) return null;

  return {
    id: 'debt-payments',
    name: t('builder.groups.debtPayments', { ns: 'onboarding' }),
    categories,
    enabled: true,
    isCustom: false
  };
}

export function buildDebtCategoriesPayload(
  debts: DebtAnswer[],
  t: TranslateFn,
  budgetCurrency = 'USD',
  exchangeRates?: ExchangeRates | null,
  now = new Date()
) {
  const group = buildDebtCategoryGroup(debts, t, budgetCurrency, exchangeRates, now);
  if (!group) return [];
  return toCreateCategoriesPayload([group]);
}

export function buildGoalCategoryGroup(
  goals: GoalAnswer[],
  t: TranslateFn,
  budgetCurrency = 'USD',
  exchangeRates?: ExchangeRates | null,
  options?: { includeContributions?: boolean },
  now = new Date()
): CategoryGroupDraft | null {
  const includeContributions = options?.includeContributions !== false;
  const billCat = (name: string, bill?: RecurringAmount) =>
    cat(name, bill, now, budgetCurrency, exchangeRates);

  const categories = goals
    .filter(goal => goal.name.trim())
    .map(goal => {
      const contribution = includeContributions ? goal.monthlyContribution : undefined;
      return billCat(goal.name.trim(), contribution);
    })
    .filter(category => category.name);

  if (categories.length === 0) return null;

  return {
    id: 'savings-goals',
    name: t('builder.groups.savingsGoals', { ns: 'onboarding' }),
    categories,
    enabled: true,
    isCustom: false
  };
}

export function buildGoalCategoriesPayload(
  goals: GoalAnswer[],
  t: TranslateFn,
  budgetCurrency = 'USD',
  exchangeRates?: ExchangeRates | null,
  options?: { includeContributions?: boolean },
  now = new Date()
) {
  const group = buildGoalCategoryGroup(
    goals,
    t,
    budgetCurrency,
    exchangeRates,
    options,
    now
  );
  if (!group) return [];
  return toCreateCategoriesPayload([group]);
}

export function countSelectedCategories(groups: CategoryGroupDraft[]): number {
  return groups
    .filter(group => group.enabled)
    .reduce((total, group) => total + group.categories.length, 0);
}

export function toCreateCategoriesPayload(groups: CategoryGroupDraft[]) {
  return groups
    .filter(group => group.enabled && group.name.trim() && group.categories.length > 0)
    .map(group => ({
      name: group.name.trim(),
      isIncome: Boolean(group.isIncome),
      categories: group.categories
        .map(category => ({
          name: category.name.trim(),
          note: category.note?.trim() || undefined
        }))
        .filter(category => category.name)
    }));
}
